import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTabsModule } from '@angular/material/tabs';
import { type JobExecutionStatus } from '@cdw-ae/common-data';
import { CdwButtonModule } from '@cdw-ae/common-ui-components-button';
import { CdwIconModule } from '@cdw-ae/common-ui-components-icon';
import {
  JOB_TYPE_DISPLAY_NAMES,
  selectAllCompletedJobsLoading,
  selectAllJobsMerged,
  selectMyCompletedJobsLoading,
  selectMyJobsMerged,
  setActiveTab,
} from '@cdw-ae/jobs-status-state';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { JobCardComponent } from '../job-card/job-card.component';

interface JobDisplayItem {
  execution: JobExecutionStatus;
  progress?: number;
  statusText: string;
  statusColor: string;
  displayName: string;
}

const TAB_INDEX = {
  MY_JOBS: 0,
  ALL_JOBS: 1,
} as const;

@Component({
  selector: 'app-jobs-panel',
  standalone: true,
  imports: [
    CommonModule,
    MatTabsModule,
    MatProgressSpinnerModule,
    CdwButtonModule,
    CdwIconModule,
    JobCardComponent,
  ],
  templateUrl: './jobs-panel.component.html',
  styleUrls: ['./jobs-panel.component.scss'],
})
export class JobsPanelComponent implements OnInit, OnDestroy {
  @Output() closePanel = new EventEmitter<void>();

  myJobs: JobDisplayItem[] = [];
  allJobs: JobDisplayItem[] = [];
  myJobsLoading = false;
  allJobsLoading = false;
  activeTabIndex = TAB_INDEX.MY_JOBS;
  private destroy$ = new Subject<void>();

  constructor(private store: Store) {}

  ngOnInit(): void {
    // Subscribe to myJobs from store (merged: in-progress from Firestore + completed from SQL)
    // Limited to 15 jobs total
    this.store
      .select(selectMyJobsMerged)
      .pipe(takeUntil(this.destroy$))
      .subscribe((jobs) => {
        this.myJobs = jobs.map((execution) => this.mapToDisplayItem(execution));
      });

    // Subscribe to allJobs from store (merged: in-progress from Firestore + completed from SQL)
    // Limited to 15 jobs total
    this.store
      .select(selectAllJobsMerged)
      .pipe(takeUntil(this.destroy$))
      .subscribe((jobs) => {
        this.allJobs = jobs.map((execution) => this.mapToDisplayItem(execution));
      });

    // Subscribe to loading states
    this.store
      .select(selectMyCompletedJobsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.myJobsLoading = loading;
      });

    this.store
      .select(selectAllCompletedJobsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.allJobsLoading = loading;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  close(): void {
    this.closePanel.emit();
  }

  onTabChange(index: number): void {
    this.store.dispatch(
      setActiveTab({ tab: index === TAB_INDEX.MY_JOBS ? 'my' : 'all' })
    );
  }

  private mapToDisplayItem(execution: JobExecutionStatus): JobDisplayItem {
    let displayName = this.getWorkflowDisplayName(execution.workflowName);

    // Override display name based on jobType if available
    const jobType = execution.additionalJobData?.jobType;
    if (typeof jobType === 'string' && jobType in JOB_TYPE_DISPLAY_NAMES) {
      displayName =
        JOB_TYPE_DISPLAY_NAMES[jobType as keyof typeof JOB_TYPE_DISPLAY_NAMES];
    }

    return {
      execution,
      progress: undefined, // Progress tracking not yet implemented
      statusText: '', // Provided by job-card component
      statusColor: '', // Provided by job-card component
      displayName,
    };
  }

  private getWorkflowDisplayName(workflowName: string | undefined): string {
    if (!workflowName) {
      return 'Job';
    }

    // Convert workflow name to title case as fallback
    // e.g., "device-assets-update-service-workflow" -> "Device Assets Update Service"
    return workflowName
      .replace(/-workflow$/, '')
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }
}
